//interface
import {
    SwiggyInstrumentsPaymentMethodData,
    SwiggyInstrumentsData,
    SuggestedAmounts,
    ViolationType,
    ViolationState,
    ValidationInfo,
    PaymentMethodMeta,
    PaymentMethod,
} from "@payment-ui/interfaces";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";

//helpers
import { getPaymentAriaLabel, getPaymentDisableAriaLabel } from "@payment-ui/helpers/Accessibility";
import { getPaymentMethodIconUrl } from "./Payment";

export const getSwiggyInstrumentAriaLabel = ({
    paymentMethodData,
    swiggyInstrumentsData,
    isSelected,
    isEnabled,
}: {
    paymentMethodData: SwiggyInstrumentsPaymentMethodData;
    swiggyInstrumentsData: SwiggyInstrumentsData;
    isSelected: boolean;
    isEnabled: boolean;
}): string => {
    if (!isEnabled) {
        return getPaymentDisableAriaLabel(paymentMethodData);
    }

    const showLessBalanceText = !swiggyInstrumentsData.isBalanceSufficient;
    return getPaymentAriaLabel({
        paymentMethodData,
        paymentData: swiggyInstrumentsData,
        balance: swiggyInstrumentsData.eligibleBalance,
        showLessBalanceText,
        isSelected,
    });
};

export const getAmountViolationInfo = (amount: number, validationInfo: ValidationInfo): ViolationState => {
    const { creditValidation, debitValidation, cashbackValidation, minErrorValidation } = validationInfo;

    // check if amount entered is less than 1 (static limit coming from backend)
    if (amount < minErrorValidation.limit) {
        return {
            type: ViolationType.ERROR,
            message: minErrorValidation.message,
            showSwiggyMoneyInfoHalfCard: minErrorValidation.showLearnMoreHalfCard,
        };
    }

    // error: check if amount entered is more than available credit limit
    if (amount > creditValidation.limit) {
        return {
            type: ViolationType.ERROR,
            message: creditValidation.message,
            showSwiggyMoneyInfoHalfCard: creditValidation.showLearnMoreHalfCard,
        };
    }

    // warning: check if amount entered is more than available cashback limit (credit limit - min cashback available)
    if (amount > cashbackValidation.limit) {
        return {
            type: ViolationType.WARNING,
            message: cashbackValidation.message,
            showSwiggyMoneyInfoHalfCard: cashbackValidation.showLearnMoreHalfCard,
        };
    }

    // warning: check if amount entered is more than available debit limit
    if (amount > debitValidation.limit) {
        return {
            type: ViolationType.WARNING,
            message: debitValidation.message,
            showSwiggyMoneyInfoHalfCard: debitValidation.showLearnMoreHalfCard,
        };
    }

    return {
        type: ViolationType.NONE,
        message: "",
        showSwiggyMoneyInfoHalfCard: false,
    };
};

export const getSwiggyInstrumentIconUrl = (paymentMethod: SwiggyInstrumentsPaymentMethodData): string => {
    const isIconEnabled = paymentMethod.enabled || !!paymentMethod.meta.monthlyDebitLimitExceeded;
    return getPaymentMethodIconUrl({ ...paymentMethod, enabled: isIconEnabled });
};

export const isInfoMessageVisible = (
    swiggyInstrumentPaymentMethod: SwiggyInstrumentsPaymentMethodData,
    isSwiggyInstrumentSelected: boolean,
    isPaymentMethodEnabled: boolean,
): boolean => {
    const {
        monthlyDebitLimitExceeded,
        disableMessage,
        learnMoreHalfCard,
        infoMessage,
    } = swiggyInstrumentPaymentMethod.meta;

    //when debit limit has exceeded then info message would be a combination of disableMessage and learnMoreHalfCard
    if (monthlyDebitLimitExceeded && (disableMessage || learnMoreHalfCard)) {
        return true;
    }
    //When debit limit has not exceeded then info message would be a combination of infoMessage and learnMoreHalfCard
    //if either of them is not present returning false
    if (!infoMessage && !learnMoreHalfCard) {
        return false;
    }
    //when content of info message is there and swiggy instrument is selected and enabled, returning true
    if (isSwiggyInstrumentSelected && isPaymentMethodEnabled) {
        return true;
    }
    return false;
};

export const getInfoMessage = (swiggyInstrumentPaymentMethod: SwiggyInstrumentsPaymentMethodData): string => {
    const { monthlyDebitLimitExceeded, disableMessage, infoMessage } = swiggyInstrumentPaymentMethod.meta;
    if (monthlyDebitLimitExceeded) {
        return disableMessage ?? "";
    }
    return infoMessage ?? "";
};

export const isLowBalanceAlertVisible = (
    swiggyInstrumentsData: SwiggyInstrumentsData,
    isSwiggyInstrumentSelected: boolean,
    isPaymentMethodEnabled: boolean,
): boolean => isPaymentMethodEnabled && !swiggyInstrumentsData.isBalanceSufficient && !isSwiggyInstrumentSelected;

export const isDebitLimitExceeded = (swiggyInstrumentPaymentMethod: SwiggyInstrumentsPaymentMethodData): boolean =>
    !!swiggyInstrumentPaymentMethod.meta.monthlyDebitLimitExceeded;

export const isDisabledMessageVisible = (
    isPaymentMethodEnabled: boolean,
    swiggyInstrumentPaymentMethod: SwiggyInstrumentsPaymentMethodData,
): boolean => !isPaymentMethodEnabled && !swiggyInstrumentPaymentMethod.meta.monthlyDebitLimitExceeded;
// returns if 'OTHER' is selected in add swiggy money options

export const isOtherAmount = (selectedAmount: number, suggestedAmounts: SuggestedAmounts[]): boolean => {
    const found = suggestedAmounts.find(option => {
        return option.amount == selectedAmount;
    });

    return !found;
};

export const isSwiggyInstrumentPaymentMethod = (
    method: PaymentMethod<PaymentMethodMeta>,
): method is SwiggyInstrumentsPaymentMethodData =>
    method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS;
